/**
 * Operator Analytics — post-game insight across every played live event.
 *
 * Surfaces the analysis we'd otherwise run by hand against the DB:
 *   - top-line participation KPIs
 *   - per-event leaderboard (the real standings, doubling included)
 *   - per-round difficulty (avg points earned, normalised to the round max)
 *   - per-round "swing" (spread) — which rounds actually separate teams
 *   - mechanic flags (bonus usage, visual-round drift)
 *
 * All data comes from GET /api/analytics/overview (admin-only). Charts are
 * plain Radix Box bars — no charting dependency, matches the design system.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Flex, Grid, Heading, Text, Badge, Select, Separator, Callout, Button,
} from '@radix-ui/themes';
import api from '../../utils/api';

type RoundType = 'STANDARD' | 'AUDIO' | 'VISUAL';

interface RoundStat {
  roundIndex: number;
  title: string;
  type: RoundType;
  avgPoints: number;
  spread: number;
  best: number;
  maxPoints: number;
}

interface StandingRow {
  teamId: string;
  teamName: string;
  playerCount: number;
  doubledRound: number | null;
  grandTotal: number;
  rank: number;
}

interface EventAnalytics {
  id: string;
  quizName: string;
  venueName: string | null;
  date: string;
  status: string;
  roundLevelOnly: boolean;
  teamCount: number;
  activeTeamCount: number;
  leftEarlyCount: number;
  playerCount: number;
  scoreCount: number;
  doubledCount: number;
  bonusRowCount: number;
  winnerName: string | null;
  winnerScore: number | null;
  standings: StandingRow[];
  rounds: RoundStat[];
}

interface Summary {
  eventCount: number;
  teamCount: number;
  playerCount: number;
  scoreCount: number;
}

interface OverviewResponse {
  status: string;
  data: { summary: Summary; events: EventAnalytics[] };
}

// --- Per-event drill-down detail (GET /api/analytics/event/:id/detail) ---

interface DetailQuestion {
  id: string;
  orderIndex: number;
  text: string;
  attempts: number;
  correct: number;
  partial: number;
  wrong: number;
  correctRate: number | null; // 0-100 integer percent, or null (e.g. VISUAL)
}

interface DetailRound {
  roundIndex: number;
  title: string;
  type: RoundType;
  questionCount: number;
  playedTeamCount: number;
  avgPoints: number;
  spread: number;
  questions: DetailQuestion[];
  distribution: number[] | null;
}

interface ExtremeQuestion {
  id: string;
  orderIndex: number;
  text: string;
  correctRate: number | null;
  attempts: number;
  correct: number;
  partial: number;
  wrong: number;
  roundIndex: number;
  roundTitle: string;
}

interface EventDetail {
  event: {
    id: string;
    quizName: string;
    venueName: string | null;
    date: string;
    activeTeamCount: number;
  };
  rounds: DetailRound[];
  hardest: ExtremeQuestion[];
  easiest: ExtremeQuestion[];
}

interface DetailResponse {
  status: string;
  data: EventDetail;
}

// Radix accent per round type so the format's shape reads at a glance.
const TYPE_COLOR: Record<RoundType, 'blue' | 'amber' | 'purple'> = {
  STANDARD: 'blue',
  AUDIO: 'amber',
  VISUAL: 'purple',
};

const CSS_VAR: Record<RoundType, string> = {
  STANDARD: 'var(--blue-9)',
  AUDIO: 'var(--amber-9)',
  VISUAL: 'var(--purple-9)',
};

function formatDate(s: string): string {
  return new Date(s).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

// Green→amber→red CSS var for a correct-rate %, so the bar's colour reflects
// difficulty at a glance (high rate = easy = green, low = hard = red).
function rateColor(rate: number): string {
  if (rate >= 66) return 'var(--grass-9)';
  if (rate >= 33) return 'var(--amber-9)';
  return 'var(--red-9)';
}

function truncate(s: string, n = 80): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

// A single horizontal bar: filled fraction of a track, with an inline value
// label. `color` is any CSS color/var; `track` is the empty background.
function Bar({
  value, max, color = 'var(--accent-9)', label,
}: { value: number; max: number; color?: string; label?: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <Flex align="center" gap="2" width="100%">
      <Box
        style={{
          position: 'relative', flex: 1, height: 10, borderRadius: 5,
          background: 'var(--gray-4)', overflow: 'hidden',
        }}
      >
        <Box style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 5 }} />
      </Box>
      {label !== undefined && (
        <Text size="1" color="gray" style={{ minWidth: 56, textAlign: 'right' }}>
          {label}
        </Text>
      )}
    </Flex>
  );
}

function KpiCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card size="2">
      <Flex direction="column" gap="1">
        <Text size="1" color="gray" weight="medium">{label}</Text>
        <Heading size="6">{value}</Heading>
      </Flex>
    </Card>
  );
}

const AnalyticsDashboard: React.FC = () => {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [events, setEvents] = useState<EventAnalytics[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-event drill-down, fetched lazily when the selection changes.
  const [detail, setDetail] = useState<EventDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [openRounds, setOpenRounds] = useState<Set<number>>(new Set());

  useEffect(() => {
    let mounted = true;
    api
      .get<OverviewResponse>('/api/analytics/overview')
      .then((res) => {
        if (!mounted) return;
        const payload = res.data?.data;
        setSummary(payload?.summary ?? null);
        setEvents(payload?.events ?? []);
        setSelectedId(payload?.events?.[0]?.id ?? '');
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        setError('Could not load analytics.');
        setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  // Lazily load the drill-down detail whenever the selected event changes.
  // Round-level historical imports have no per-question data, so skip the
  // fetch entirely and let the render show a note instead.
  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    const ev = events.find((e) => e.id === selectedId);
    if (ev?.roundLevelOnly) {
      setDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }
    let mounted = true;
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    setOpenRounds(new Set());
    api
      .get<DetailResponse>(`/api/analytics/event/${selectedId}/detail`)
      .then((res) => {
        if (!mounted) return;
        setDetail(res.data?.data ?? null);
        setDetailLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        setDetailError('Could not load question-level detail for this event.');
        setDetailLoading(false);
      });
    return () => { mounted = false; };
  }, [selectedId, events]);

  const toggleRound = (idx: number) => {
    setOpenRounds((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const selected = useMemo(
    () => events.find((e) => e.id === selectedId) ?? null,
    [events, selectedId],
  );

  // Highest grand total in the selected event → leaderboard bar scale.
  const topScore = useMemo(
    () => (selected ? Math.max(...selected.standings.map((s) => s.grandTotal), 1) : 1),
    [selected],
  );

  // Visual-round drift signal: avg spread on visual rounds. Near-zero means
  // everyone maxes them and they no longer differentiate the field.
  const visualDrift = useMemo(() => {
    if (!selected) return null;
    const vis = selected.rounds.filter((r) => r.type === 'VISUAL');
    if (vis.length === 0) return null;
    return vis.reduce((s, r) => s + r.spread, 0) / vis.length;
  }, [selected]);

  if (loading) {
    return (
      <Box p="6"><Text color="gray">Loading analytics…</Text></Box>
    );
  }

  if (error) {
    return (
      <Box p="6">
        <Callout.Root color="red"><Callout.Text>{error}</Callout.Text></Callout.Root>
      </Box>
    );
  }

  if (!summary || events.length === 0) {
    return (
      <Box p="6">
        <Heading size="6" mb="2">Game Analytics</Heading>
        <Text color="gray">No played events yet — once a game has teams and scores, it’ll show up here.</Text>
      </Box>
    );
  }

  return (
    <Box p={{ initial: '4', md: '6' }} style={{ maxWidth: 1100, margin: '0 auto' }}>
      <Heading size="7" mb="1">Game Analytics</Heading>
      <Text color="gray" size="2">Across {summary.eventCount} played events</Text>

      {/* Top-line KPIs */}
      <Grid columns={{ initial: '2', sm: '4' }} gap="3" mt="4">
        <KpiCard label="Events played" value={summary.eventCount} />
        <KpiCard label="Teams" value={summary.teamCount} />
        <KpiCard label="Players" value={summary.playerCount} />
        <KpiCard label="Scores recorded" value={summary.scoreCount.toLocaleString()} />
      </Grid>

      {/* Event picker */}
      <Flex align="center" gap="3" mt="6" mb="4" wrap="wrap">
        <Text size="2" weight="medium">Event</Text>
        <Select.Root value={selectedId} onValueChange={setSelectedId}>
          <Select.Trigger />
          <Select.Content>
            {events.map((e) => (
              <Select.Item key={e.id} value={e.id}>
                {formatDate(e.date)} · {e.venueName ?? 'Unknown'} · {e.quizName}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Flex>

      {selected && (
        <Flex direction="column" gap="4">
          {/* Event header + sub-stats */}
          <Card size="3">
            <Flex justify="between" align="start" wrap="wrap" gap="3">
              <Box>
                <Flex align="center" gap="2">
                  <Heading size="5">{selected.quizName}</Heading>
                  {selected.roundLevelOnly && (
                    <Badge color="gray" variant="soft" title="Imported from the old scoresheets — round totals only">
                      Round-level import
                    </Badge>
                  )}
                </Flex>
                <Text color="gray" size="2">
                  {formatDate(selected.date)}{selected.venueName ? ` · ${selected.venueName}` : ''}
                </Text>
              </Box>
              {selected.winnerName && (
                <Box style={{ textAlign: 'right' }}>
                  <Text size="1" color="gray" weight="medium">🏆 Winner</Text>
                  <Heading size="4">{selected.winnerName}</Heading>
                  <Text color="gray" size="2">{selected.winnerScore} pts</Text>
                </Box>
              )}
            </Flex>
            <Separator size="4" my="3" />
            <Grid columns={{ initial: '2', sm: '4' }} gap="3">
              <Stat label="Teams" value={`${selected.activeTeamCount}${selected.leftEarlyCount ? ` (+${selected.leftEarlyCount} left)` : ''}`} />
              <Stat label="Players" value={selected.playerCount} />
              <Stat label="Doubling used" value={`${selected.doubledCount}/${selected.activeTeamCount}`} />
              <Stat label="Scores" value={selected.scoreCount.toLocaleString()} />
            </Grid>
          </Card>

          {/* Mechanic flags */}
          {(selected.bonusRowCount <= 1 || (visualDrift !== null && visualDrift < 1.5)) && (
            <Callout.Root color="amber" size="1">
              <Callout.Text>
                {selected.bonusRowCount <= 1 && 'Bonus mechanic effectively unused this game. '}
                {visualDrift !== null && visualDrift < 1.5 &&
                  `Visual round barely separated teams (spread ${visualDrift.toFixed(1)}) — nearly everyone maxed it.`}
              </Callout.Text>
            </Callout.Root>
          )}

          <Grid columns={{ initial: '1', md: '2' }} gap="4">
            {/* Leaderboard */}
            <Card size="3">
              <Heading size="3" mb="3">Leaderboard</Heading>
              <Flex direction="column" gap="2">
                {selected.standings.map((s) => (
                  <Flex key={s.teamId} align="center" gap="3">
                    <Text size="1" color="gray" style={{ width: 22, textAlign: 'right' }}>{s.rank}</Text>
                    <Box style={{ width: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Text size="2">{s.teamName}</Text>
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Bar value={s.grandTotal} max={topScore} label={`${s.grandTotal}`} />
                    </Box>
                  </Flex>
                ))}
              </Flex>
            </Card>

            {/* Per-round difficulty */}
            <Card size="3">
              <Heading size="3" mb="1">Round difficulty</Heading>
              <Text size="1" color="gray" mb="3">Avg points earned vs. round max</Text>
              <Flex direction="column" gap="3">
                {selected.rounds.map((r) => (
                  <Box key={r.roundIndex}>
                    <Flex justify="between" align="center" mb="1">
                      <Flex align="center" gap="2">
                        <Badge color={TYPE_COLOR[r.type]} variant="soft" size="1">{r.type}</Badge>
                        <Text size="2">{r.roundIndex}. {r.title}</Text>
                      </Flex>
                      <Text size="1" color="gray">
                        {r.maxPoints > 0 ? `${Math.round((r.avgPoints / r.maxPoints) * 100)}%` : '—'}
                      </Text>
                    </Flex>
                    <Bar
                      value={r.avgPoints}
                      max={r.maxPoints || r.best || 1}
                      color={CSS_VAR[r.type]}
                      label={`${r.avgPoints}/${r.maxPoints}`}
                    />
                  </Box>
                ))}
              </Flex>
            </Card>
          </Grid>

          {/* Swing rounds */}
          <Card size="3">
            <Heading size="3" mb="1">Swing rounds</Heading>
            <Text size="1" color="gray" mb="3">
              Spread of team scores per round — taller bars are where games are won or lost; flat bars didn’t separate anyone.
            </Text>
            <Flex direction="column" gap="2">
              {[...selected.rounds]
                .sort((a, b) => b.spread - a.spread)
                .map((r) => (
                  <Flex key={r.roundIndex} align="center" gap="3">
                    <Box style={{ width: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Text size="2">{r.roundIndex}. {r.title}</Text>
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Bar value={r.spread} max={maxSpread(selected.rounds)} color={CSS_VAR[r.type]} label={`σ ${r.spread}`} />
                    </Box>
                  </Flex>
                ))}
            </Flex>
          </Card>

          {/* ---- Per-event question-level drill-down ---- */}
          <Separator size="4" my="2" />
          <Heading size="5">Question-level detail</Heading>

          {selected.roundLevelOnly && (
            <Callout.Root color="gray" size="1">
              <Callout.Text>
                Round-level historical import — no per-question data. The leaderboard and per-round
                stats above are exact; the old scoresheets only recorded round totals, not which
                questions teams got right.
              </Callout.Text>
            </Callout.Root>
          )}

          {!selected.roundLevelOnly && detailLoading && (
            <Text color="gray" size="2">Loading question-level detail…</Text>
          )}

          {!detailLoading && detailError && (
            <Callout.Root color="red" size="1">
              <Callout.Text>{detailError}</Callout.Text>
            </Callout.Root>
          )}

          {!detailLoading && !detailError && detail && (
            <>
              {/* Hardest / Easiest questions */}
              <Grid columns={{ initial: '1', md: '2' }} gap="4">
                <ExtremesCard
                  title="Hardest questions"
                  subtitle="Lowest correct rate"
                  accent="red"
                  questions={detail.hardest}
                />
                <ExtremesCard
                  title="Easiest questions"
                  subtitle="Highest correct rate"
                  accent="grass"
                  questions={detail.easiest}
                />
              </Grid>

              {/* Per-round detail (collapsible) */}
              <Card size="3">
                <Heading size="3" mb="1">Per-round breakdown</Heading>
                <Text size="1" color="gray" mb="3">
                  Expand a round for per-question correct rates and the spread of how many teams aced N questions.
                </Text>
                <Flex direction="column" gap="2">
                  {detail.rounds.map((r) => {
                    const open = openRounds.has(r.roundIndex);
                    return (
                      <Box
                        key={r.roundIndex}
                        style={{
                          border: '1px solid var(--gray-5)', borderRadius: 8, overflow: 'hidden',
                        }}
                      >
                        <Button
                          variant="ghost"
                          color="gray"
                          onClick={() => toggleRound(r.roundIndex)}
                          style={{
                            width: '100%', margin: 0, padding: '10px 12px',
                            borderRadius: 0, justifyContent: 'flex-start',
                          }}
                        >
                          <Flex align="center" gap="2" width="100%" wrap="wrap">
                            <Text size="2" color="gray" style={{ width: 14 }}>{open ? '▾' : '▸'}</Text>
                            <Badge color={TYPE_COLOR[r.type]} variant="soft" size="1">{r.type}</Badge>
                            <Text size="2" weight="medium" style={{ flex: 1, textAlign: 'left' }}>
                              {r.roundIndex}. {r.title}
                            </Text>
                            <Text size="1" color="gray">
                              avg {r.avgPoints} · σ {r.spread} · {r.playedTeamCount} teams
                            </Text>
                          </Flex>
                        </Button>

                        {open && (
                          <Box p="3" style={{ borderTop: '1px solid var(--gray-5)' }}>
                            {r.type === 'VISUAL' || r.questions.length === 0 ? (
                              <Text size="2" color="gray">
                                No per-question data for this round
                                {r.type === 'VISUAL' ? ' (visual rounds are scored as a whole).' : '.'}
                              </Text>
                            ) : (
                              <Flex direction="column" gap="2">
                                {r.questions.map((q) => (
                                  <Flex key={q.id} align="center" gap="3">
                                    <Text size="1" color="gray" style={{ width: 22, textAlign: 'right' }}>
                                      {q.orderIndex}
                                    </Text>
                                    <Box
                                      title={q.text}
                                      style={{
                                        width: 220, overflow: 'hidden',
                                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                      }}
                                    >
                                      <Text size="2">{q.text}</Text>
                                    </Box>
                                    <Box style={{ flex: 1 }}>
                                      {q.correctRate === null ? (
                                        <Text size="1" color="gray">no rate</Text>
                                      ) : (
                                        <Bar
                                          value={q.correctRate}
                                          max={100}
                                          color={rateColor(q.correctRate)}
                                          label={`${q.correctRate}% · ${q.correct}✓ ${q.partial}~ ${q.wrong}✗`}
                                        />
                                      )}
                                    </Box>
                                  </Flex>
                                ))}
                              </Flex>
                            )}

                            {/* Distribution mini bar-chart */}
                            {r.distribution && r.distribution.length > 0 && (
                              <Box mt="4">
                                <Text size="1" color="gray" weight="medium">
                                  Teams by # questions fully correct
                                </Text>
                                <Distribution data={r.distribution} color={CSS_VAR[r.type]} />
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Flex>
              </Card>
            </>
          )}
        </Flex>
      )}
    </Box>
  );
};

// Hardest / Easiest column. `accent` is a Radix color name used for the rate text.
function ExtremesCard({
  title, subtitle, accent, questions,
}: {
  title: string;
  subtitle: string;
  accent: 'red' | 'grass';
  questions: ExtremeQuestion[];
}) {
  return (
    <Card size="3">
      <Heading size="3" mb="1">{title}</Heading>
      <Text size="1" color="gray" mb="3">{subtitle}</Text>
      {questions.length === 0 ? (
        <Text size="2" color="gray">No ranked questions for this event.</Text>
      ) : (
        <Flex direction="column" gap="2">
          {questions.map((q) => (
            <Flex key={q.id} align="center" gap="3" title={q.text}>
              <Badge color={accent} variant="soft" size="2" style={{ minWidth: 48, justifyContent: 'center' }}>
                {q.correctRate === null ? '—' : `${q.correctRate}%`}
              </Badge>
              <Box style={{ flex: 1, overflow: 'hidden' }}>
                <Text size="2" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {truncate(q.text)}
                </Text>
                <Text size="1" color="gray">R{q.roundIndex} · {q.roundTitle}</Text>
              </Box>
            </Flex>
          ))}
        </Flex>
      )}
    </Card>
  );
}

// Small vertical column chart for a round's correct-count distribution.
// data[k] = number of teams that got exactly k questions fully correct.
function Distribution({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data, 1);
  return (
    <Flex align="end" gap="2" mt="2" style={{ height: 90 }}>
      {data.map((count, k) => {
        const h = max > 0 ? (count / max) * 70 : 0;
        return (
          <Flex key={k} direction="column" align="center" gap="1" style={{ flex: 1, minWidth: 18 }}>
            <Text size="1" color="gray">{count}</Text>
            <Box
              title={`${count} team${count === 1 ? '' : 's'} got ${k} correct`}
              style={{
                width: '100%', maxWidth: 28, height: Math.max(2, h),
                background: color, borderRadius: 3,
              }}
            />
            <Text size="1" color="gray">{k}</Text>
          </Flex>
        );
      })}
    </Flex>
  );
}

function maxSpread(rounds: RoundStat[]): number {
  return Math.max(...rounds.map((r) => r.spread), 1);
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <Flex direction="column" gap="1">
      <Text size="1" color="gray" weight="medium">{label}</Text>
      <Text size="4" weight="bold">{value}</Text>
    </Flex>
  );
}

export default AnalyticsDashboard;
